import { trackingIntegrationListQuerySchema, type TrackingDevicesResult, type TrackingIntegration } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useToast } from '../../../shared/hooks/use-toast';
import { createDeviceMapping, listIntegrationDevices, listIntegrations } from '../integrations.api';
import { apiErrorReason } from '../policies/api-error-reason.policy';

export interface DeviceMappingFormValues {
  integrationId: string;
  deviceId: string;
  truckId: string;
  /** Picked as a date; blank means from now. */
  effectiveFrom?: { toISOString(): string } | null;
}

export type DevicesState = { status: 'idle' } | { status: 'loading' } | { status: 'loaded'; result: TrackingDevicesResult } | { status: 'error' };

/** The devices fetched for one integration; a different selected integration means they are stale, which reads as loading. */
type FetchedDevices = { integrationId: string; result: TrackingDevicesResult | null };

/** Installations to map to (an enabled or disabled one; the first 100, a deployment holds a handful), the selected one's devices, and the save. */
export function useDeviceMappingSheet(onDone: () => void) {
  const toast = useToast();
  const [integrations, setIntegrations] = useState<TrackingIntegration[]>([]);
  const [integrationId, setIntegrationId] = useState<string | undefined>(undefined);
  const [fetched, setFetched] = useState<FetchedDevices | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listIntegrations(trackingIntegrationListQuerySchema.parse({ pageSize: 100 }))
      .then((page) => !cancelled && setIntegrations(page.items))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!integrationId) return;
    let cancelled = false;
    listIntegrationDevices(integrationId)
      .then((result) => !cancelled && setFetched({ integrationId, result }))
      .catch(() => !cancelled && setFetched({ integrationId, result: null }));
    return () => {
      cancelled = true;
    };
  }, [integrationId]);

  const devices = devicesState(integrationId, fetched);

  async function save(values: DeviceMappingFormValues): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const mapping = await createDeviceMapping({
        integrationId: values.integrationId,
        deviceId: values.deviceId.trim(),
        truckId: values.truckId,
        effectiveFrom: values.effectiveFrom?.toISOString(),
      });
      toast.success(`${mapping.deviceId} mapped to ${mapping.truckRegistration}`);
      onDone();
    } catch (err) {
      setError(apiErrorReason(err, 'Could not save the mapping. Check the fields and try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  return { integrations, selectIntegration: setIntegrationId, devices, submitting, error, save };
}

function devicesState(integrationId: string | undefined, fetched: FetchedDevices | undefined): DevicesState {
  if (!integrationId) return { status: 'idle' };
  if (fetched?.integrationId !== integrationId) return { status: 'loading' };
  return fetched.result ? { status: 'loaded', result: fetched.result } : { status: 'error' };
}
